import {
  Container,
  Alert,
  Skeleton,
  Stack,
  Typography,
  Box,
  Paper,
  Divider,
  Grid,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import StopIcon from '@mui/icons-material/Stop';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate } from 'react-router-dom';

import RunCard from './RunCard';
import { AppiumAutomationPagesHeader } from '../appiumAutomationPagesComponents';
import { useActiveRunsLogic } from './useActiveRunsLogic';
import { RunSummaryExport } from './export';

export default function ActiveRunsPage() {
  const navigate = useNavigate();
  const {
    activeRuns,
    currentRun,
    error,
    handleCancelAll,
    handleCancelRun,
    isCancelling,
    loading,
    queuedRuns,
    recentRuns,
  } = useActiveRunsLogic();

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h4" mb={3}>
          Active Runs
        </Typography>
        <Stack spacing={2}>
          <Skeleton height={200} />
          <Skeleton height={120} />
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3, pt: 0 }}>
      <AppiumAutomationPagesHeader />

      <Grid container spacing={3}>
        {/* Running */}
        <Grid item xs={12} lg={8}>
          <Box mb={4}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <PlayArrowIcon color="warning" />
                <Typography variant="h6">Running ({activeRuns.length})</Typography>
              </Box>
              {currentRun && (
                <Tooltip title="Abort current run">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={isCancelling ? <CircularProgress size={16} /> : <StopIcon />}
                    onClick={() => handleCancelRun(currentRun.runId)}
                    disabled={isCancelling}
                  >
                    Abort
                  </Button>
                </Tooltip>
              )}
            </Box>
            {activeRuns.length > 0 ? (
              activeRuns.map((run) => <RunCard key={run.runId} run={run} showLogs />)
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No tests currently running</Typography>
              </Paper>
            )}
          </Box>

          {/* Recent */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" mb={2}>
                Recent Runs
              </Typography>
              <RunSummaryExport />
            </Box>
            {recentRuns.length > 0 ? (
              recentRuns.map((run) => <RunCard key={run.runId} run={run} />)
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No recent runs</Typography>
              </Paper>
            )}
          </Box>
        </Grid>

        {/* Queue */}
        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <HourglassEmptyIcon color="info" />
                <Typography variant="h6">Queue ({queuedRuns.length})</Typography>
              </Box>

              {(activeRuns.length > 0 || queuedRuns.length > 0) && (
                <Tooltip title="Cancel current + queued runs">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={isCancelling ? <CircularProgress size={16} /> : <CancelIcon />}
                    onClick={handleCancelAll}
                    disabled={isCancelling}
                  >
                    Cancel Suite
                  </Button>
                </Tooltip>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {queuedRuns.length > 0 ? (
              queuedRuns.map((run, idx) => (
                <Box
                  key={run.runId}
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 24 }}>
                    #{idx + 1}
                  </Typography>

                  {run.platform === 'ios' ? (
                    <AppleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  ) : (
                    <AndroidIcon fontSize="small" sx={{ color: 'success.light' }} />
                  )}

                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {run.scenarioId || run.flowId}
                    </Typography>
                    {run.scenarioId && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {run.flowId}
                      </Typography>
                    )}
                  </Box>

                  <Chip label="Queued" size="small" color="info" variant="outlined" />

                  <Tooltip title="Remove from queue">
                    <IconButton
                      size="small"
                      onClick={() => handleCancelRun(run.runId)}
                      disabled={isCancelling}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))
            ) : (
              <Typography color="text.secondary" textAlign="center" py={2}>
                No scenarios in queue
              </Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate('/active-runs/summary')}
              sx={{
                bgcolor: 'info.main',
                color: 'custom.surfaceAlt',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                py: 1,
              }}
            >
              View Test Summary
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
